<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">流失預警</h1>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <label>流失風險</label>
        <select v-model="filters.churnRisk" class="form-control" @change="fetchData">
          <option value="">全部</option>
          <option value="high">高風險</option>
          <option value="medium">中風險</option>
          <option value="low">低風險</option>
        </select>
      </div>
      <button class="btn btn-primary" @click="fetchData">查詢</button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchData">重試</button>
    </div>

    <div v-else class="dashboard-content">
      <!-- 統計卡片 -->
      <div v-if="statistics" class="stats-cards">
        <div class="stat-card">
          <div class="stat-label">總會員數</div>
          <div class="stat-value">{{ statistics.totalMembers || 0 }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">流失會員數</div>
          <div class="stat-value">{{ statistics.churnedMembers || 0 }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">流失率</div>
          <div class="stat-value">{{ (statistics.churnRate * 100).toFixed(2) }}%</div>
        </div>
      </div>

      <!-- 風險分布圖 -->
      <div v-if="statistics && statistics.riskDistribution" class="chart-section">
        <div class="chart-card">
          <PieChart
            title="流失風險分布"
            :data="riskDistributionData"
            nameKey="risk"
            valueKey="count"
            height="300px"
          />
        </div>
      </div>

      <!-- 流失會員列表 -->
      <div class="table-section">
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>會員姓名</th>
                <th>電話</th>
                <th>Email</th>
                <th>流失風險</th>
                <th>流失機率</th>
                <th>最後購買日期</th>
                <th>預測 CLV</th>
                <th>總消費</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="items.length === 0">
                <td colspan="8" class="empty-state">沒有找到資料</td>
              </tr>
              <tr v-for="item in items" :key="item.memberId">
                <td>{{ item.memberName || '-' }}</td>
                <td>{{ item.memberPhone || '-' }}</td>
                <td>{{ item.memberEmail || '-' }}</td>
                <td>
                  <span :class="getRiskBadgeClass(item.churnRisk)">
                    {{ getRiskText(item.churnRisk) }}
                  </span>
                </td>
                <td>{{ item.churnProbability ? (item.churnProbability * 100).toFixed(1) + '%' : '-' }}</td>
                <td>{{ formatDate(item.lastPurchaseDate) }}</td>
                <td>{{ formatCurrency(item.predictedClv) }}</td>
                <td>{{ formatCurrency(item.totalSpent) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <ListPagination
        v-if="pagination"
        :page="pagination.page"
        :total-pages="pagination.totalPages"
        @change="goToPage"
      />
    </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { analyticsService } from '@/api/modules/analytics.js';
import { showError } from '@/utils/swal.js';
import PieChart from '@/components/charts/PieChart.vue';

export default {
  name: 'ChurnAnalysis',
  components: {
    PieChart
  },
  setup() {
    const loading = ref(false);
    const error = ref(null);
    const items = ref([]);
    const pagination = ref(null);
    const statistics = ref(null);
    const filters = ref({
      churnRisk: ''
    });

    const riskDistributionData = computed(() => {
      if (!statistics.value || !statistics.value.riskDistribution) return [];
      return [
        { risk: '高風險', count: statistics.value.riskDistribution.high || 0 },
        { risk: '中風險', count: statistics.value.riskDistribution.medium || 0 },
        { risk: '低風險', count: statistics.value.riskDistribution.low || 0 }
      ];
    });

    const fetchData = async () => {
      try {
        loading.value = true;
        error.value = null;
        
        // 過濾空值
        const params = {
          page: pagination.value?.page || 1,
          pageSize: 20
        };
        
        // 只加入非空值
        if (filters.value.churnRisk && filters.value.churnRisk.trim() !== '') {
          params.churnRisk = filters.value.churnRisk;
        }
        if (filters.value.sortBy && filters.value.sortBy.trim() !== '') {
          params.sortBy = filters.value.sortBy;
        }
        if (filters.value.order && filters.value.order.trim() !== '') {
          params.order = filters.value.order;
        }
        
        // 同時取得列表和統計
        const [listResponse, statsResponse] = await Promise.all([
          analyticsService.getChurnMembers(params),
          analyticsService.getChurnStatistics()
        ]);

        if (listResponse.success) {
          items.value = listResponse.data.items || [];
          pagination.value = listResponse.data.pagination;
        }

        if (statsResponse.success) {
          statistics.value = statsResponse.data;
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '取得資料失敗';
        showError('錯誤', error.value);
      } finally {
        loading.value = false;
      }
    };

    const goToPage = (page) => {
      if (pagination.value) {
        pagination.value.page = page;
        fetchData();
      }
    };

    const getRiskBadgeClass = (risk) => {
      if (risk === 'high') return 'badge badge-danger';
      if (risk === 'medium') return 'badge badge-warning';
      return 'badge badge-success';
    };

    const getRiskText = (risk) => {
      const riskMap = {
        'high': '高風險',
        'medium': '中風險',
        'low': '低風險'
      };
      return riskMap[risk] || risk;
    };

    const formatCurrency = (value) => {
      if (!value && value !== 0) return '-';
      return `$${parseInt(value).toLocaleString()}`;
    };

    const formatDate = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('zh-TW');
    };

    onMounted(() => {
      fetchData();
    });

    return {
      loading,
      error,
      items,
      pagination,
      statistics,
      filters,
      riskDistributionData,
      fetchData,
      goToPage,
      getRiskBadgeClass,
      getRiskText,
      formatCurrency,
      formatDate
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stat-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 32px;
  font-weight: 600;
  color: #333;
}

.chart-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.chart-card {
  width: 100%;
}

.table-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
